/**
 * TenantsSidebar — usada no split panel de Admin → Sites.
 *
 * Só faz sentido pra super_admin (que enxerga múltiplos tenants).
 * Pra admin tenant, a página omite essa sidebar e mostra cards direto.
 */

import clsx from "clsx";
import { Building2, Package } from "lucide-react";

import type { Site, Tenant } from "@/types";

export type SelectedTenantNode =
  | { kind: "all" }
  | { kind: "tenant"; id: string };

interface Props {
  tenants: Tenant[];
  sites: Site[];  // pra contar sites por tenant
  selected: SelectedTenantNode;
  onSelect: (node: SelectedTenantNode) => void;
}

export function TenantsSidebar({ tenants, sites, selected, onSelect }: Props) {
  const countByTenant = new Map<string, number>();
  for (const s of sites) {
    countByTenant.set(s.tenant_id, (countByTenant.get(s.tenant_id) ?? 0) + 1);
  }

  return (
    <nav className="space-y-1 text-sm" aria-label="Tenants">
      <NodeButton
        label="Todos os sites"
        icon={<Package className="h-4 w-4" />}
        count={sites.length}
        active={selected.kind === "all"}
        onClick={() => onSelect({ kind: "all" })}
      />

      {tenants.map((t) => {
        const count = countByTenant.get(t.id) ?? 0;
        const isActive = selected.kind === "tenant" && selected.id === t.id;
        return (
          <NodeButton
            key={t.id}
            label={t.name}
            subLabel={t.slug}
            icon={<Building2 className="h-4 w-4 text-brand-600 dark:text-brand-400" />}
            count={count}
            active={isActive}
            onClick={() => onSelect({ kind: "tenant", id: t.id })}
          />
        );
      })}
    </nav>
  );
}

function NodeButton({
  label, subLabel, icon, count, active, onClick,
}: {
  label: string;
  subLabel?: string;
  icon: React.ReactNode;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
        active
          ? "bg-brand-100/70 dark:bg-brand-900/40 font-semibold text-brand-900 dark:text-brand-100"
          : "text-slate-700 dark:text-slate-300 hover:bg-surface-100/70 dark:hover:bg-slate-800/60",
      )}
    >
      {icon}
      <span className="flex-1 min-w-0">
        <span className="block truncate">{label}</span>
        {subLabel && (
          <code className="block truncate text-[10px] text-slate-500 dark:text-slate-400">
            {subLabel}
          </code>
        )}
      </span>
      <span className="tabular-nums text-[11px] text-slate-500 dark:text-slate-400">
        {count}
      </span>
    </button>
  );
}
